import type { SanityClient } from '@sanity/client';

// Cached singleton so we don't re-import the (heavy) @sanity/client chunk on
// subsequent calls within the same context.
let client: SanityClient | null = null;
let pending: Promise<SanityClient> | null = null;

/**
 * Returns the Sanity client. The underlying `@sanity/client` package (~9.7 KiB
 * gzipped of unused-in-initial-render code per Lighthouse) is loaded via a
 * dynamic import so it only ends up in the bundle of the chunks that actually
 * need it — keeping it out of the critical entry path.
 *
 * NOTE: The `type` import above is erased at build time, so it does NOT pull
 * the runtime module into the initial bundle.
 */
export async function useSanityClient(): Promise<SanityClient> {
    if (client) {
        return client;
    }
    if (pending) {
        return pending;
    }

    const config = useRuntimeConfig();

    pending = import('@sanity/client').then(({ createClient }) => {
        client = createClient({
            projectId: config.public.sanity.projectId,
            dataset: config.public.sanity.dataset,
            apiVersion: config.public.sanity.apiVersion,
            useCdn: true,
        });
        return client;
    });

    return pending;
}
